/* =========================================================================
   estate-access-gate.tsx — module_access entry gate for the estate.

   Applies ONLY to Supabase-authenticated sessions: a successful
   `module_access` query with no `campus_lease_studio` row renders
   AccessDenied. Query errors are unavailable/retry states: protected content
   is never rendered unless a successful row belongs to the current user.
   Passcode fallback mode (local/dev convenience) is never gated.
   ========================================================================= */

import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./auth-context";
import { supabase, isSupabaseConfigured } from "./supabase-client";
import { THIS_MODULE, openEstateApp } from "./estate-sso";
import { Wordmark } from "./wordmark";

type GateState = "checking" | "granted" | "denied" | "unavailable";

type BoundGateState = {
  state: GateState;
  userId?: string;
  error?: string;
};

function AccessDenied({ email }: Readonly<{ email?: string }>) {
  return (
    <main className="auth-page" aria-labelledby="access-denied-title">
      <section className="auth-card" role="alert">
        <div className="auth-logo">
          <Wordmark size="md" />
        </div>
        <h1 id="access-denied-title">No access to TENURE</h1>
        <p>
          {email ? `${email} is` : "Your account is"} signed in to Stratosfyre,
          but this module hasn&apos;t been enabled for it yet.
        </p>
        <p className="p-small">
          Ask your Stratosfyre administrator to grant the{" "}
          <code>campus_lease_studio</code> module, or head back to the estate.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void openEstateApp("website")}
        >
          Back to Stratosfyre
        </button>
      </section>
    </main>
  );
}

function AccessUnavailable({
  error,
  onRetry,
}: Readonly<{ error?: string; onRetry: () => void }>) {
  return (
    <main className="auth-page" aria-labelledby="access-unavailable-title">
      <section className="auth-card" role="alert">
        <div className="auth-logo">
          <Wordmark size="md" />
        </div>
        <h1 id="access-unavailable-title">TENURE access is unavailable</h1>
        <p>
          We could not verify your module access. Protected content remains
          unavailable until the check succeeds.
        </p>
        {error && <p className="p-small">{error}</p>}
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Retry access check
        </button>
      </section>
    </main>
  );
}

export function EstateAccessGate({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const auth = useAuth();
  // Gate only real Supabase sessions; passcode fallback stays ungated.
  const gated =
    !auth.usePasscodeFallback && isSupabaseConfigured() && !!auth.user;
  const userId = auth.user?.id;
  const [boundState, setBoundState] = useState<BoundGateState>(() => ({
    state: gated ? "checking" : "granted",
    userId: gated ? userId : undefined,
  }));
  const [retry, setRetry] = useState(0);
  const requestVersion = useRef(0);

  useEffect(() => {
    if (!gated || !userId) {
      setBoundState({ state: "granted" });
      return;
    }
    let cancelled = false;
    const version = ++requestVersion.current;
    const apply = (next: BoundGateState) => {
      if (!cancelled && requestVersion.current === version) setBoundState(next);
    };
    apply({ state: "checking", userId });

    try {
      const query = supabase
        .from("module_access")
        .select("module")
        .eq("user_id", userId)
        .eq("module", THIS_MODULE)
        .maybeSingle();
      void Promise.resolve(query)
        .then(({ data, error }) => {
          if (error) {
            apply({ state: "unavailable", userId, error: error.message });
            return;
          }
          apply({ state: data ? "granted" : "denied", userId });
        })
        .catch((error: unknown) =>
          apply({
            state: "unavailable",
            userId,
            error:
              error instanceof Error
                ? error.message
                : "The access service did not return a result.",
          }),
        );
    } catch (error) {
      apply({
        state: "unavailable",
        userId,
        error:
          error instanceof Error
            ? error.message
            : "The access service did not start.",
      });
    }

    return () => {
      cancelled = true;
      if (requestVersion.current === version) requestVersion.current += 1;
    };
  }, [gated, retry, userId]);

  // A previously granted result cannot authorize a different authenticated
  // identity during the render before its new lookup effect begins.
  const state =
    gated && boundState.userId !== userId ? "checking" : boundState.state;
  if (state === "denied") return <AccessDenied email={auth.email} />;
  if (state === "unavailable")
    return (
      <AccessUnavailable
        error={boundState.error}
        onRetry={() => {
          setBoundState({ state: "checking", userId });
          setRetry((count) => count + 1);
        }}
      />
    );
  if (state === "checking") return null;
  return <>{children}</>;
}
